import React, { useEffect, useState } from 'react';
import {
  Box, Button, Grid, Paper, TextField, Typography,
  MenuItem, Select, InputLabel, FormControl,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, Alert
} from '@mui/material';
import { listUsuarios, createUsuario, updateUsuario, deleteUsuario } from '../../services/usuarios';

const Usuarios = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [usuarioToDelete, setUsuarioToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [q, setQ] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [form, setForm] = useState({
    id: '',
    nome: '',
    email: '',
    senha: '',
    tipo: '',
    cliente_id: '',
    status: 'Ativo',
    ultimo_acesso: '',
    data_criacao: '',
    data_atualizacao: '',
  });

  const handleOpen = (data) => {
    if (data) {
      setForm({
        id: data.id || '',
        nome: data.nome || '',
        email: data.email || '',
        senha: data.senha || '',
        tipo: data.tipo || '',
        cliente_id: data.cliente_id || data.cliente || '',
        status: data.status || (data.ativo === 1 ? 'Ativo' : 'Inativo'),
        ultimo_acesso: data.ultimo_acesso || '',
        data_criacao: data.data_criacao || data.dataCriacao || '',
        data_atualizacao: data.data_atualizacao || '',
      });
    } else {
      setForm({
        id: '', nome: '', email: '', senha: '', tipo: '', cliente_id: '', status: 'Ativo',
        ultimo_acesso: '', data_criacao: '', data_atualizacao: '',
      });
    }
    setOpenDialog(true);
  };
  const handleClose = () => setOpenDialog(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));
  const askDelete = (user) => {
    setUsuarioToDelete(user);
    setOpenDelete(true);
  };
  const closeDelete = () => {
    setUsuarioToDelete(null);
    setOpenDelete(false);
  };
  const loadUsuarios = async () => {
    try {
      setLoading(true);
      const data = await listUsuarios();
      setUsuarios(Array.isArray(data) ? data : []);
      // Força recalcular layout após popular a tabela (corrige caso em que só aparece com DevTools aberto)
      if (typeof window !== 'undefined') {
        requestAnimationFrame(() => {
          try { window.dispatchEvent(new Event('resize')); } catch (_) {}
        });
      }
    } catch (e) {
      console.error('Erro ao carregar usuários', e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadUsuarios();
  }, []);

  const handleSave = async () => {
    try {
      setLoading(true);
      // validações simples
      const emailOk = /.+@.+\..+/.test(String(form.email || ''));
      if (!form.nome || !emailOk) {
        showSnack('Informe um nome e um e-mail válidos.', 'error');
        setLoading(false);
        return;
      }
      const payload = { ...form };
      if (payload.id && (!payload.senha || payload.senha === '')) {
        delete payload.senha; // não atualizar senha se vazia
      }
      // Backend mapeia 'status' para 'ativo' automaticamente
      if (payload?.id) {
        await updateUsuario(payload.id, payload);
        showSnack('Usuário atualizado com sucesso.');
      } else {
        await createUsuario(payload);
        showSnack('Usuário criado com sucesso.');
      }
      await loadUsuarios();
      setOpenDialog(false);
    } catch (e) {
      console.error('Erro ao salvar usuário', e);
      showSnack('Erro ao salvar usuário.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!usuarioToDelete?.id) return closeDelete();
    try {
      setLoading(true);
      await deleteUsuario(usuarioToDelete.id);
      await loadUsuarios();
      closeDelete();
      showSnack('Usuário excluído com sucesso.');
    } catch (e) {
      console.error('Erro ao excluir usuário', e);
      showSnack('Erro ao excluir usuário.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ py: 4, px: { xs: 2, md: 3 }, width: '100%', maxWidth: '100% !important', boxSizing: 'border-box' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Typography variant="h4" component="h1">Gerenciar Usuários</Typography>
        <Button variant="contained" onClick={() => handleOpen()}>Novo Usuário</Button>
      </Box>
      <Paper sx={{ p: 2, width: '100%', maxWidth: '100%' }}>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              label="Buscar usuário"
              size="small"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="tipo-label">Tipo</InputLabel>
              <Select labelId="tipo-label" label="Tipo" defaultValue="">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="admin">Administrador</MenuItem>
                <MenuItem value="gerente">Gerente</MenuItem>
                <MenuItem value="operador">Operador</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-label">Status</InputLabel>
              <Select labelId="status-label" label="Status" defaultValue="">
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="ativo">Ativo</MenuItem>
                <MenuItem value="inativo">Inativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined">Limpar</Button>
          </Grid>
        </Grid>
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'auto', willChange: 'transform', transform: 'translateZ(0)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Nome</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>E-mail</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Tipo</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Cliente ID</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Status</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Último acesso</th>
                <th style={{ textAlign: 'left', padding: 8, borderBottom: '1px solid #eee' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} style={{ padding: 12 }}>Carregando...</td>
                </tr>
              )}
              {!loading && usuarios?.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 12 }}>Nenhum usuário encontrado.</td>
                </tr>
              )}
              {!loading && usuarios
                ?.filter((u) => {
                  const t = (q || '').toLowerCase();
                  if (!t) return true;
                  const alvo = `${u.nome || ''} ${u.email || ''}`.toLowerCase();
                  return alvo.includes(t);
                })
                .map((u) => (
                <tr key={u.id}>
                  <td style={{ padding: 8 }}>{u.nome}</td>
                  <td style={{ padding: 8 }}>{u.email}</td>
                  <td style={{ padding: 8 }}>{u.tipo || '-'}</td>
                  <td style={{ padding: 8 }}>{u.cliente_id || u.cliente || '-'}</td>
                  <td style={{ padding: 8 }}>{u.status}</td>
                  <td style={{ padding: 8 }}>{u.ultimo_acesso ? String(u.ultimo_acesso).replace('T', ' ').slice(0, 19) : '-'}</td>
                  <td style={{ padding: 8 }}>
                    <Button size="small" onClick={() => handleOpen(u)} sx={{ mr: 1 }}>Editar</Button>
                    <Button size="small" color="error" onClick={() => askDelete(u)}>Excluir</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>

      {/* Dialog de Usuário */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{form?.id ? 'Editar Usuário' : 'Novo Usuário'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {form?.id && (
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="ID" name="id" value={form.id} InputProps={{ readOnly: true }} />
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome"
                name="nome"
                value={form.nome}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="E-mail"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="tipo-label">Tipo</InputLabel>
                <Select
                  labelId="tipo-label"
                  label="Tipo"
                  name="tipo"
                  value={form.tipo}
                  onChange={handleChange}
                >
                  <MenuItem value="admin">Administrador</MenuItem>
                  <MenuItem value="gerente">Gerente</MenuItem>
                  <MenuItem value="operador">Operador</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cliente ID"
                name="cliente_id"
                value={form.cliente_id}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="status-label">Status</InputLabel>
                <Select
                  labelId="status-label"
                  label="Status"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  <MenuItem value="Ativo">Ativo</MenuItem>
                  <MenuItem value="Inativo">Inativo</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Senha"
                name="senha"
                type="password"
                value={form.senha}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Último acesso"
                name="ultimo_acesso"
                value={form.ultimo_acesso || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Criado em"
                name="data_criacao"
                value={form.data_criacao || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Atualizado em"
                name="data_atualizacao"
                value={form.data_atualizacao || ''}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={form.nome === "" || form.email === "" || form.tipo === "" || form.status === "" || (form.senha === "" && !form.id)}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Confirmação de exclusão */}
      <Dialog open={openDelete} onClose={closeDelete}>
        <DialogTitle>Excluir usuário</DialogTitle>
        <DialogContent>
          <Typography>Tem certeza que deseja excluir o usuário "{usuarioToDelete?.nome}"?</Typography>
        </DialogContent>
        <DialogActions>
        <Button onClick={closeDelete}>Cancelar</Button>
        <Button color="error" variant="contained" onClick={handleDelete}>Excluir</Button>
      </DialogActions>
    </Dialog>

    {/* Snackbar de feedback */}
    <Snackbar open={snack.open} autoHideDuration={3000} onClose={closeSnack} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert onClose={closeSnack} severity={snack.severity} variant="filled" sx={{ width: '100%' }}>
        {snack.message}
      </Alert>
    </Snackbar>
    </Box>
  );
};

export default Usuarios;
